import React from "react";
import { useFormikContext } from "formik";

import { IS_BROWSER } from "@config/consts";
import { HOMEPAGE_V2 } from "@config/testIds/app";

import { LOADING_STATE } from "@typings/reduxThunkActions";

import { Link } from "@components/Navigation/Link";

import { DrawerManager } from "@features/DrawerManager/DrawerManager.www";

import { iOS } from "@services/iOS";

import { useBreakpoint } from "@hooks/useBreakpoint";

import { FIELD, FormValues } from "../ServiceSelector.types";
import { Select } from "../Select";
import { Field } from "../Field";

import { ZipCodeFieldProps } from "./ZipCodeField.types";
import * as Styled from "./ZipCodeField.styled";
import { suggestionsReducer, useHelpers } from "./ZipCodeField.helpers";
import { getSuggestions } from "./ZipCodeField.getSuggestions";

export const ZipCodeField: React.FC<ZipCodeFieldProps> = props => {
  const drawerId = React.useId();
  const { values, isSubmitting, setFieldValue, submitForm } =
    useFormikContext<FormValues>();

  const [state, dispatch] = React.useReducer(suggestionsReducer, {
    status: LOADING_STATE.IDLE,
    suggestions: [],
  });
  const { hideDrawer, isDrawerActive } = DrawerManager.useManager();
  const { mobile, largeMobile } = useBreakpoint();
  const {
    changeHandler,
    keyDownHandler,
    buttonClickHandler,
    focusHandler,
    blurHandler,
    clickHandler,
    drawerInputChangeHandler,
    drawerCloseHandler,
  } = useHelpers(dispatch, state, {
    drawerId,
  });

  const isIOS = IS_BROWSER ? iOS() : false;

  React.useEffect(() => {
    if (!values[FIELD.ZIPCODE].length) return;

    getSuggestions({
      serviceType: values[FIELD.SERVICE],
      query: values[FIELD.ZIPCODE],
      dispatch,
    });
  }, [values[FIELD.SERVICE]]);

  React.useEffect(() => {
    if (
      (!(mobile || largeMobile) || isSubmitting) &&
      isDrawerActive(drawerId)
    ) {
      hideDrawer(drawerId);
    }
  }, [mobile, largeMobile, isSubmitting]);

  return (
    <React.Fragment>
      <Styled.Root data-drawer-id={drawerId}>
        <Styled.MobileButton type="button" onClick={clickHandler} />

        <Select
          testId={HOMEPAGE_V2.ZIP_INPUT}
          name={FIELD.ZIPCODE}
          position={props.resultsPosition}
          options={state.suggestions}
          placeholder={props.placeholder}
          onChange={changeHandler}
          onSelect={() => {
            setFieldValue(FIELD.COVERED, true);
            submitForm();
          }}
          onKeyDown={keyDownHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
          isSearchable
        />
      </Styled.Root>

      <DrawerManager
        id={drawerId}
        variant="arrow"
        noAnimation={isIOS}
        preventIOSKeyboardScroll
        onClose={drawerCloseHandler}
      >
        <Styled.DrawerBody>
          {props.drawerHeader}

          <Field
            type="text"
            name={FIELD.ZIPCODE}
            placeholder={props.placeholder}
            onKeyDown={keyDownHandler}
            onChange={drawerInputChangeHandler}
            autoComplete="off"
            autoFocus
          />

          {state.status === LOADING_STATE.SUCCEEDED && (
            <Styled.DrawerList>
              {!!state.suggestions.length &&
                state.suggestions.map(suggestion => (
                  <li key={suggestion.value}>
                    <Link
                      as="button"
                      type="button"
                      onClick={buttonClickHandler(suggestion.value)}
                      $style="regular_16"
                    >
                      {suggestion.label}
                    </Link>
                  </li>
                ))}

              {!state.suggestions.length && !!values[FIELD.ZIPCODE] && (
                <li>
                  <Link
                    as="button"
                    type="button"
                    onClick={buttonClickHandler(values[FIELD.ZIPCODE], false)}
                    $style="regular_16"
                  >
                    {values[FIELD.ZIPCODE]}
                  </Link>
                </li>
              )}
            </Styled.DrawerList>
          )}
        </Styled.DrawerBody>
      </DrawerManager>
    </React.Fragment>
  );
};
